import { envValue } from '@helpers/envHelpers';

/**
 * Where the value sent as `state` is kept between leaving for the identity
 * provider and coming back, so the callback can reject a response it did not
 * start itself.
 */
export const OIDC_STATE_KEY = 'oidc_login_state';

function randomToken(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Where the provider sends the browser back. Derived from the host being used
 * rather than read from configuration: review apps inherit the beta config and
 * override only VITE_BASE_URL, so a configured value would send every preview
 * environment back to beta.
 *
 * The provider matches this as a literal string, so the trailing slash has to
 * survive: it must be registered exactly as built here.
 */
export function oidcRedirectUri(): string {
  return `${window.location.origin}/oidc/consume/`;
}

/** Builds the authorization URL and remembers the state it was started with. */
export function startOidcLogin(): string {
  const state = randomToken();
  window.sessionStorage.setItem(OIDC_STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: envValue('VITE_OIDC_CLIENT_ID') || '',
    redirect_uri: oidcRedirectUri(),
    scope: 'openid email profile',
    state,
    nonce: randomToken(),
  });

  return `https://${envValue('VITE_OIDC_HOST')}/oauth2/authorize?${params.toString()}`;
}

/** Reads the state we started with and clears it so it cannot be replayed. */
export function consumeExpectedState(): string | null {
  const expected = window.sessionStorage.getItem(OIDC_STATE_KEY);
  window.sessionStorage.removeItem(OIDC_STATE_KEY);
  return expected;
}
